import React, { useState, useEffect } from 'react';
import PageTitle from '../../components/PageTitle';
import { api } from '../../../utils/index';
import SeoConfiguration from './SeoConfiguration';
import SeoPreview from './SeoPreview';
import Loading from '../../components/Loading';
import DimensionLinking from './DimensionLinking';
import MetricLinking from './MetricLinking';
import { getDimensionGroups } from "../../api_methods";
import { Card, CardBody, CardHeader, Col, Row, Button } from "reactstrap";
import EmptyTranslations from "../TranslationUtility/EmptyTranslations";
import UnsavedRules from "./UnsavedRules/UnsavedRules";
import Header from "../../../components/Headers/Header";
import {extractData, notify, prefix} from "../../../utils/index_tagging_tool";
import {Tab, Tabs} from "react-bootstrap";
import CloneReport from "./CloneReport";

const SeoReport = (props) => {
  const cardWidth = 9; // This can be a const if it doesn't change

  const [reportId] = useState(props.match.params.id); // reportId doesn't change after initial set
  const [report, setReport] = useState(null);
  const [savedDimensions, setSavedDimensions] = useState([]);
  const [dimensionGroups, setDimensionGroups] = useState([]);
  const [savedMetrics, setSavedMetrics] = useState([]);
  const [running, setRunning] = useState(false)
  // metricGroup was in state but not used in the render or methods, so I've omitted it.
  // If it's needed for other logic, it can be added back.

  useEffect(() => {
    // Fetch report details
    api.get(`/tagging-tool/reports/${reportId}/`)
      .then(extractData)
      .then((data) => {
        setReport(data);
        const groupsDimensions = data.groups.reduce((accumulator, currentGroup) => {
          return accumulator.concat(currentGroup.dimensions);
        }, []); // The '[]' is the initial empty array to start with
        setSavedDimensions(groupsDimensions)

      })
      .catch(console.error); // Added basic error handling

        // Fetch dimension groups
    getDimensionGroups()
      .then((data) => setDimensionGroups(data))
      .catch(console.error);

    // Fetch saved metrics
    api.get(`/tagging-tool/saved-metrics/`)
      .then(extractData)
      .then((data) => setSavedMetrics(data))
      .catch(console.error);
  }, [reportId]); // Dependency array ensures this runs when reportId changes (though it's from props.match)

  const updateReport = (updatedReport) => {
    setReport(updatedReport);
  };

    // Funzione per avviare un report da interfaccia. Le date passate sono fittizie per i seo, perchè i report sono indipendenti dalle date
  const runReport = async () => {
    setRunning(true)

    api.post(
      prefix +
        `reports/${reportId}/run/`,
      { credentials: 'include' }
    )
      .then(extractData)
      .then((data) => {
        if (data === "Report running")
          notify(
            "La run del report è partita correttamente",
            "success"
          );
        else if (data === 'Report already running')
          notify('Il report è già stato avviato da qualcun altro.', 'error')
        else
          notify(
            'Il report è andato in errore, contattare un innovation.',
            'error'
          )
        setRunning(false)
      })
      .catch(console.log)
  }

  // Conditional title setting
  const pageTitle = report ? `${reportId} - ${report.name} - ${report.status}` : 'Loading Report...';


  if (report === null) {
    return (
      <>
        <Row className="justify-content-center">
          <Col className="mb-20" style={{ marginBottom: '100px' }} xs={cardWidth}>
            <Card className="shadow">
              <Loading child={null}></Loading>
            </Card>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <>
      <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
      <Row style={{marginBottom: "100px"}}>
        <Col md={10}>
          <Card className="bg-secondary shadow flex">
            <CardHeader>
              <PageTitle
                title={pageTitle}
                link={`/reporting/`}
                linkText={'Torna alla lista dei report'}
              />
            </CardHeader>
            <CardBody className="pt-0 pt-md-4">
              <Tabs defaultActiveKey="settings">
              {/* Blocco iniziale che consente di modificare la configurazione del report, e di esegure la run */}
              <Tab title="⚙️ Configurazione" eventKey="settings" justify>
                <SeoConfiguration
                  reportId={report.id}
                  reportFile={report.input_options.path}
                  reportClient={report.input_options.client || ''}
                  reportLanguage={
                    report.input_options.language_code || ''
                  }
                  reportLingua={report.input_options.language || ''}
                  reportKw={report.input_options.kw_column}
                  reportVol={report.input_options.vol_column}
                  reportOutput={report.output_options}
                  reportName={report.name}
                  reportGroups={report.groups}
                  groupOptions={dimensionGroups.map((d) => {
                    return { label: d.name, value: d.name };
                  })}
                  updateReport={updateReport}
                  seo={report.seo}
                />
              </Tab>
              <Tab title="🧩 Dimensioni" eventKey="dimensions">
                {/* Blocco intermedio per la gestione delle dimensioni del report (aggiunta/eliminazione, modifica del nome/tipo/thanos */}
                <DimensionLinking
                  dimensions={report.dimensions}
                  // Filtro le dimensioni salvate
                  savedDimensions={savedDimensions}
                  reportName={report.name}
                  seo={true}
                />
              </Tab>
              <Tab title="🔦 Metriche" eventKey="metrics">
                <MetricLinking
                  metrics={report.custom_metrics}
                  savedMetrics={savedMetrics}
                  reportName={report.name}
                  seo={true}
                />
              </Tab>
              <Tab title="📁 Stato del report" eventKey="status">
                <p className="mb-3 mt-3">
                  In questa sezione sono presenti funzionalità per osservare lo stato di alcune caratteristiche
                  del report. All'interno ogni sottosezione si potrà trovare una spiegazione dettagliata.
                </p>
                <EmptyTranslations identifier={report.id} info={report} group={false} />
                <UnsavedRules identifier={report.id} info={report} />
              </Tab>

              <Tab title="🕹️ Simulazione" eventKey="simulation">
                {/* Blocco finale per la simulazione del report */}
                <SeoPreview
                  reportId={report.id}
                  reportName={report.name}
                  dimensions={report.dimensions}
                  ignoredColumns={
                    report.input_options.ignore_for_simulation
                  }
                />
              </Tab>
                </Tabs>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="bg-secondary shadow flex">
            <CardBody>
              <Row className="justify-content-center">
                <CloneReport id={reportId}/>
              </Row>
              <Row className="justify-content-center mt-3">
                {running ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : (
                  <Button color="primary" onClick={() => runReport()} block>
                    <i className="fas fa-upload"></i> Salva su BigQuery
                  </Button>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SeoReport;